<template>
  <Dialog
    ref="dialog"
    dialog-mode="medium"
    :class="['mobile-full payment-modal', isMobile ? 'flyout-right' : '']"
    content-class="dialog-content"
    @close="closeDialog"
  >
    <template #title>
      <div class="text-[20px] font-semibold">{{ title }}</div>
    </template>
    <template #content>
      <div class="flex flex-col gap-5">
        <div v-if="card" class="items-center justify-between rounded-md bg-[--color-neutral-a5] px-2.5 py-3.5">
          <div class="flex gap-2.5 font-medium">
            <CardLabels :type="card.cardType" />
            <div class="flex-col font-medium">
              {{ $t("shared.payment.bank_card_form.card_ending_in") }} {{ card.maskedCardNumber.slice(-4) }} |
              {{ $t("common.labels.expiration") }} {{ card.expirationMonth }}/{{ card.expirationYear }}
            </div>
          </div>
        </div>
        <div v-if="card" class="flex items-center gap-2">
          <VcIcon name="information-circle" class="" :size="18"></VcIcon>
          <span class="font-semibold">{{ $t("pages.account.payments.edit_payment_info") }}</span>
        </div>
        <PaymentProcessingCyberSource
          ref="paymentProcessingCyberSourceRefs"
          :card="card"
          :is-default="props.isDefault"
          is-part-of-modal
          @card-saved="closeDialog"
        />
        <div class="mt-5 flex flex-col gap-5">
          <div v-if="addresses.length > 0" class="flex flex-wrap items-center justify-between">
            <span class="text-xl font-bold">{{
              $t("shared.checkout.billing_details_section.labels.billing_address")
            }}</span>
            <VcButton
              class="address-btn m-0 justify-items-end !p-0"
              size="lg"
              variant="link"
              @click="handleAddressFormExpandedState()"
            >
              <VcIcon v-if="!isAddressFormExpanded" name="plus-alt" class="mr-2" :size="16"></VcIcon>
              <span class="font-medium">{{ handleAddressFormButtonText }}</span>
            </VcButton>
          </div>
          <div v-if="!isAddressFormExpanded" class="md:px-7">
            <div class="selected-address flex flex-col gap-5 md:-mx-7">
              <div
                v-for="(address, index) in addressesWithPreselectedAddressFirst.slice(0, nbOfItemsToDisplay())"
                :key="index"
                class="selected-address__item flex items-start justify-between rounded-md border border-[--color-neutral-a4] p-5"
              >
                <VcRadioButton
                  :id="address.id"
                  v-model="userSelectedAddress"
                  name="paymentModalAddresses"
                  :value="address.id!"
                >
                  <ul class="flex max-w-[250px] flex-col font-medium leading-6">
                    <!-- Japan-specific address format -->
                    <template v-if="address.countryCode === 'JPN'">
                      <li>{{ address.postalCode }}</li>
                      <li>{{ address.regionName }}</li>
                      <li>{{ address.city }}</li>
                      <li>{{ address.line1 }}</li>
                      <li v-if="address.buildingTL">{{ address.buildingTL }}</li>
                      <li>{{ address.countryName }}</li>
                      <li class="mt-2">
                        {{ address.phone }}
                      </li>
                      <li class="break-all">{{ address.email }}</li>
                    </template>
                    <!-- China address format -->
                    <template v-else-if="address.countryCode === 'CHN'">
                      <li class="mb-1 font-bold">{{ address.lastName }} {{ address.firstName }}</li>
                      <li class="">
                        {{ address.line1 }}
                      </li>
                      <li v-if="isMUCCountry(address.countryCode!)">
                        {{ address.regionName || address.countyTL }} {{ address.postalCode }} {{ address.city }}
                      </li>
                      <li v-else>
                        {{ address.city }}, {{ address.regionName || address.countyTL }} {{ address.postalCode }}
                      </li>
                      <li>
                        {{ address.countryName }}
                      </li>
                      <li class="mb-2">
                        {{ address.phone }}
                      </li>
                      <li v-if="address.email" class="break-all">{{ address.email }}</li>
                    </template>
                    <!-- Other countries format -->
                    <template v-else>
                      <li class="font-semibold">{{ address.firstName }} {{ address.lastName }}</li>
                      <li class="">
                        {{ address.line1 }}
                      </li>
                      <li v-if="isMUCCountry(address.countryCode!)">
                        {{ address.regionName || address.countyTL }} {{ address.postalCode }} {{ address.city }}
                      </li>
                      <li v-else>
                        {{ address.city }}, {{ address.regionName || address.countyTL }} {{ address.postalCode }}
                      </li>
                      <li>
                        {{ address.countryCode }}
                      </li>
                      <li class="mb-2">
                        {{ address.phone }}
                      </li>
                      <li v-if="address.email" class="break-all">{{ address.email }}</li>
                    </template>
                  </ul>
                </VcRadioButton>
                <VcButton
                  variant="link"
                  size="lg"
                  class="text-[--link-color] underline"
                  @click="handleEditAddress(address)"
                >
                  {{ $t("common.buttons.edit") }}
                </VcButton>
              </div>
            </div>
          </div>
          <div v-if="!isAddressFormExpanded && addresses?.length >= 3">
            <VcButton
              variant="link"
              size="lg"
              class="address-btn flex items-start font-medium text-[--color-link]"
              @click="viewAllItems = !viewAllItems"
            >
              <span class="font-medium">{{ viewAllItemsText() }}</span>
              <VcIcon :name="viewAllItems ? 'chevron-up' : 'chevron-down'" class="mr-2" :size="20"></VcIcon>
            </VcButton>
          </div>
          <div
            v-if="!addressesLoading && (!addresses?.length || addresses.length == 0)"
            class="mt-5 items-center justify-between"
          >
            <div class="">
              <span class="text-xl font-bold">{{
                $t("shared.checkout.billing_details_section.labels.billing_address")
              }}</span>
            </div>
            <VcButton size="lg" variant="link" @click="handleAddressFormExpandedState()">
              <VcIcon name="plus-alt" class="mr-2" :size="16"></VcIcon>
              <span class="font-medium">{{ handleAddressFormButtonText }}</span>
            </VcButton>
          </div>
          <div v-if="isAddressFormExpanded">
            <VcAddressForm
              :model-value="editableAddress"
              :countries="countriesWithoutEmbargoed"
              :disabled="loading"
              :is-submit-disabled="isFormSubmitButtonDisabledForNewAddress"
              without-tax-exempt
              with-personal-info
              required-email
              required-city
              required-phone
              default-shipping-checkbox-hidden
              class="!px-0 md:px-7"
              @save="saveAddressAndAddCard"
            >
              <template #append="{ dirty, valid }">
                <div class="py-4">
                  <FormCaptcha
                    ref="captchaRef"
                    v-model="captchaCode"
                    element-key="captchaCode"
                    :site-key="globals.hCaptchaSiteKey"
                  />
                </div>
                <div
                  v-if="!!creditCardFormErrorMessage"
                  ref="creditCardFormError"
                  role="alert"
                  class="mb-4 mt-1 flex flex-row items-start gap-3"
                >
                  <VcIcon name="information-circle" :size="18" class="error-icon-semibold"></VcIcon>
                  <span v-html-safe="creditCardFormErrorMessage" class="error-text flex flex-col"></span>
                </div>
                <div class="flex w-full flex-col gap-4">
                  <VcButton
                    color="primary"
                    variant="solid"
                    :disabled="!dirty || !valid"
                    :loading="loading"
                    type="submit"
                    size="lg"
                  >
                    {{ formSubmitButtonText }}
                  </VcButton>

                  <VcButton class="p-4" color="secondary" variant="outline" size="lg" @click="closeDialog">
                    {{ $t("common.buttons.cancel") }}
                  </VcButton>
                </div>
              </template>
            </VcAddressForm>
          </div>
        </div>

        <div v-if="!isAddressFormExpanded" class="pt-4 md:pt-8">
          <FormCaptcha
            ref="captchaRef"
            v-model="captchaCode"
            element-key="captchaCode"
            :site-key="globals.hCaptchaSiteKey"
          />
        </div>
        <div
          v-if="!isAddressFormExpanded && !!creditCardFormErrorMessage"
          ref="creditCardFormError"
          role="alert"
          class="mt-1 flex flex-row items-start gap-3"
        >
          <VcIcon name="information-circle" :size="18" class="error-icon-semibold"></VcIcon>
          <span v-html-safe="creditCardFormErrorMessage" class="error-text flex flex-col"></span>
        </div>
        <div v-if="!isAddressFormExpanded" class="flex w-full flex-col gap-5">
          <VcButton
            color="primary"
            variant="solid"
            :disabled="isFormSubmitButtonDisabled"
            :loading="loading"
            size="lg"
            @click="handleFormSubmit()"
          >
            {{ formSubmitButtonText }}
          </VcButton>

          <VcButton class="p-4" color="secondary" variant="outline" size="lg" @click="closeDialog">
            {{ $t("common.buttons.cancel") }}
          </VcButton>
        </div>
      </div>
    </template>
  </Dialog>
  <SuggestedAddressDialog
    v-show="displaySuggestedAddressModal"
    ref="suggestedDialog"
    :suggested-address="suggestedAddress!"
    :user-typed-in-address="userTypedInAddress!"
    :existing-address-edit="existingAddressEdit!"
    is-edit-address-disabled
    @update-address="handleUpdateFromSuggestedAddressDialog"
  />
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useCountriesWithoutEmbargoed, useCurrency, useDataAreaId } from "@/core/composables";
import { AddressType } from "@/core/enums";
import { globals } from "@/core/globals";
import { isMUCCountry } from "@/core/utilities/address";
import { eventBus } from "@/shared/thorlabs/shared";
import { useUserAddresses } from "../composables";
import { useMelissaAddresses } from "../composables/useMelissaAddresses";
import type {
  MemberAddressTypeExtension,
  PaymentInstrumentDetailsType,
  QueryValidateMelissaAddressesArgs,
} from "@/core/api/graphql/types";
import SuggestedAddressDialog from "@/shared/account/components/suggested-address-dialog.vue";
import FormCaptcha from "@/shared/forms/components/form-captcha.vue";
import CardLabels from "@/shared/payment/components/card-labels.vue";
import PaymentProcessingCyberSource from "@/shared/payment/components/payment-processing-cyber-source.vue";
import Dialog from "@/shared/thorlabs/shared/components/dialog-modal.vue";

interface IProps {
  isDefault?: boolean;
}
const props = defineProps<IProps>();

const card = ref<PaymentInstrumentDetailsType | undefined>(undefined);
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const dialog = ref<InstanceType<typeof Dialog> | null>(null);
const paymentProcessingCyberSourceRefs = ref<InstanceType<typeof PaymentProcessingCyberSource> | null>(null);
const suggestedDialog = ref<InstanceType<typeof SuggestedAddressDialog> | null>(null);
const { t } = useI18n();
const { fetchAddresses, addresses, loading: addressesLoading, addOrUpdateAddresses } = useUserAddresses();
const { setBillingCountryCode, getDataAreaId, userBillingCountryCode } = useDataAreaId();
const { currentCurrency } = useCurrency();
const displaySuggestedAddressModal = ref(false);
const userSelectedAddress = ref<string | undefined>();
const userSelectedAddressDisplay = computed(() => {
  return addresses.value.find((address) => address.id === userSelectedAddress.value);
});
const { validateMelissaAddress } = useMelissaAddresses();
const existingAddressEdit = ref<MemberAddressTypeExtension>();
const suggestedAddress = ref<MemberAddressTypeExtension>();
const userTypedInAddress = ref<MemberAddressTypeExtension>();
const viewAllItems = ref(false);
const captchaCode = ref("");
const captchaRef = ref<InstanceType<typeof FormCaptcha> | null>(null);
const creditCardFormErrorMessage = ref<string>("");
const creditCardFormError = ref<HTMLElement | null>(null);
const isFormSubmitButtonDisabledForNewAddress = computed(() => {
  return !paymentProcessingCyberSourceRefs?.value?.isValidBankCard || !captchaCode.value;
});
const isFormSubmitButtonDisabled = computed(() => {
  return !paymentProcessingCyberSourceRefs?.value?.isValidBankCard || !captchaCode.value || !userSelectedAddress.value;
});
const isAddressFormExpanded = ref(false);
const handleAddressFormButtonText = computed(() => {
  return isAddressFormExpanded.value ? t("common.buttons.show_saved_addresses") : t("common.buttons.add_new_address");
});
const handleAddressFormExpandedState = async () => {
  if (!countriesWithoutEmbargoed.value.length) {
    await loadCountriesWithoutEmbargoed();
  }
  isAddressFormExpanded.value = !isAddressFormExpanded.value;
  captchaCode.value = "";
  if (!isAddressFormExpanded.value) {
    editableAddress.value = undefined;
  }
};

const handleEditAddress = async (address: MemberAddressTypeExtension) => {
  if (!countriesWithoutEmbargoed.value.length) {
    await loadCountriesWithoutEmbargoed();
  }
  editableAddress.value = { ...address };
  isAddressFormExpanded.value = true;
  captchaCode.value = "";
};
const editableAddress = ref<MemberAddressTypeExtension>();
const { countriesWithoutEmbargoed, loadCountriesWithoutEmbargoed, loading } = useCountriesWithoutEmbargoed();
const cardAddress = ref<MemberAddressTypeExtension | undefined>(undefined);
function openDialog(creditCard?: PaymentInstrumentDetailsType) {
  card.value = creditCard;
  if (creditCard) {
    cardAddress.value = addresses.value.find((address) => isSameAddress(address, card.value?.billingAddress));
    if (cardAddress.value) {
      userSelectedAddress.value = cardAddress.value.id;
    }
  }
  const currentUserSelectedAddress = addresses.value.find((address) => address.id === userSelectedAddress.value);
  const dataAreaId = getDataAreaId(
    currentCurrency.value.code,
    cardAddress.value ? cardAddress.value.countryCode ?? "" : currentUserSelectedAddress?.countryCode ?? "",
  );
  //don't wait for the loadPaymentForm method; Microform can initialize while the user is presented to the client
  //this way we avoid the 2-3 seconds when nothing happens after the add/edit card button is pressed
  paymentProcessingCyberSourceRefs.value?.loadPaymentForm(dataAreaId);
  resetCaptcha();
  dialog.value?.openDialog();
  // Scroll to the beginning of the form when dialog opens
  const dialogContent = dialog.value?.$el?.querySelector?.(".dialog-content");
  if (dialogContent) {
    dialogContent.scrollTop = 0;
  }
}

const showSuggestedAddressDialog = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  displaySuggestedAddressModal.value = true;
  suggestedDialog.value?.showDialog();
};

function closeDialog() {
  isAddressFormExpanded.value = false;
  editableAddress.value = undefined;
  eventBus.emit("hideCreditCardFormError");
  dialog.value?.closeDialog();
}

const title = computed(() =>
  card.value ? t("pages.account.payments.edit_payment_title") : t("pages.account.payments.add_new_payment_card_title"),
);

const formSubmitButtonText = computed(() => {
  return card.value ? t("common.buttons.save") : t("common.buttons.add_card");
});

function viewAllItemsText(): string {
  return viewAllItems.value ? t("common.buttons.view_less_items") : t("common.buttons.view_all_addresses");
}
function nbOfItemsToDisplay(): number {
  if (viewAllItems.value) {
    return addresses.value?.length ?? 0;
  } else {
    return addresses.value?.length && addresses.value?.length > 2 ? 2 : addresses.value?.length ?? 0;
  }
}

function recheckDataAreaId(address: MemberAddressTypeExtension) {
  const newDataAreaId = getDataAreaId(currentCurrency.value.code, address.countryCode ?? "");
  if (
    !!paymentProcessingCyberSourceRefs.value?.currentDataAreaId &&
    newDataAreaId !== paymentProcessingCyberSourceRefs.value?.currentDataAreaId
  ) {
    setBillingCountryCode(address.countryCode ?? "");
    return newDataAreaId;
  }
  return undefined;
}

async function handleFormSubmit(address?: MemberAddressTypeExtension, isNewAddress = false) {
  if (address) {
    const newDataAreaId = recheckDataAreaId(address);
    if (newDataAreaId) {
      eventBus.emit("showCreditCardFormError", {
        message: isNewAddress
          ? t("shared.payment.bank_card_form.daid_changed_address_form")
          : t("shared.payment.bank_card_form.daid_changed"),
      });
      isAddressFormExpanded.value = false;
      setMostRecentAddressAsSelected(addresses.value);
      await paymentProcessingCyberSourceRefs.value?.loadPaymentForm(newDataAreaId);
      resetCaptcha();
      return;
    }
  }
  const selectedAddress = address || userSelectedAddressDisplay.value;
  const addressWithId = !selectedAddress?.id
    ? addresses.value.find((addr) => isSameAddress(addr, selectedAddress)) ?? selectedAddress
    : selectedAddress;
  const payload = card.value
    ? {
        address: addressWithId,
        paymentInstrumentId: card.value.paymentInstrumentTokenId,
        paymentInstrumentOriginalDataAreaId: card.value.dataAreaId,
        isDefault: card.value.isDefault,
        captchaCode: captchaCode.value,
      }
    : {
        address: addressWithId,
        paymentInstrumentId: "",
        paymentInstrumentOriginalDataAreaId: "",
        isDefault: paymentProcessingCyberSourceRefs.value?.setAsDefaultPaymentCard,
        captchaCode: captchaCode.value,
      };
  eventBus.emit("addCardClicked", payload);
  resetCaptcha();
}

const saveAddressAndAddCard = async (updatedAddress: MemberAddressTypeExtension) => {
  if (isFormSubmitButtonDisabledForNewAddress.value) {
    return;
  }
  try {
    existingAddressEdit.value = updatedAddress;
    const args: QueryValidateMelissaAddressesArgs = {
      area: updatedAddress.regionId,
      city: updatedAddress.city,
      countryCode: updatedAddress.countryCode,
      line1: updatedAddress.line1,
      postalCode: updatedAddress.postalCode,
    };

    userSelectedAddress.value = undefined;
    if (updatedAddress.countryCode === "TWN" || updatedAddress.countryCode === "CHN") {
      await addOrUpdateAddresses([{ ...updatedAddress, addressType: AddressType.Billing }]);
      await handleFormSubmit(updatedAddress, true);
      return;
    }

    const result = await validateMelissaAddress(args);

    if (result?.success && result?.address) {
      const newAddress = result?.address;

      suggestedAddress.value = { ...updatedAddress };
      suggestedAddress.value.postalCode = newAddress?.postalCode || suggestedAddress.value.postalCode;
      suggestedAddress.value.line1 = newAddress?.address || suggestedAddress.value.line1;

      userTypedInAddress.value = updatedAddress;

      showSuggestedAddressDialog();
    } else {
      await addOrUpdateAddresses([{ ...updatedAddress, addressType: AddressType.Billing }]);
      await handleFormSubmit(updatedAddress, true);
    }
  } catch (error) {
    console.error("Error saving address and adding card:", error);
  }
};

const handleUpdateFromSuggestedAddressDialog = async function (updatedAddress: MemberAddressTypeExtension) {
  await handleFormSubmit(updatedAddress, true);
};

const addressesWithPreselectedAddressFirst = computed(() => {
  if (cardAddress.value) {
    return [cardAddress.value, ...addresses.value.filter((address) => address.id !== cardAddress.value?.id)];
  }
  if (addresses.value.length > 1) {
    const mostRecent = addresses.value.reduce((latest, address) =>
      new Date(address.modifiedDate) > new Date(latest.modifiedDate) ? address : latest,
    );
    return [mostRecent, ...addresses.value.filter((address) => address.id !== mostRecent.id)];
  }
  return addresses.value;
});

function setMostRecentAddressAsSelected(newAddresses: MemberAddressTypeExtension[]) {
  if (!userSelectedAddress.value) {
    const mostRecentAddress = newAddresses.reduce(
      (latest: MemberAddressTypeExtension, address: MemberAddressTypeExtension) => {
        return new Date(address.modifiedDate) > new Date(latest.modifiedDate) ? address : latest;
      },
      newAddresses[0],
    );
    if (mostRecentAddress) {
      userSelectedAddress.value = mostRecentAddress.id;
      if (addresses.value.length < 3) {
        viewAllItems.value = true;
      }
    }
  }
}

const resetCaptcha = () => {
  captchaCode.value = "";
  captchaRef.value?.reset();
};

const isSameAddress = (a: MemberAddressTypeExtension, b: MemberAddressTypeExtension | undefined) => {
  if (!a || !b) {
    return false;
  }
  return a.line1 === b.line1 && a.city === b.city && a.postalCode === b.postalCode && a.countryCode === b.countryCode;
};

onMounted(async () => {
  if (addresses.value.length === 0) {
    await fetchAddresses();
  } else {
    setMostRecentAddressAsSelected(addresses.value);
  }
});

eventBus.on("showCreditCardFormError", async (payload) => {
  creditCardFormErrorMessage.value = payload.message;
  await nextTick();
  creditCardFormError.value?.scrollIntoView({ block: "nearest" });
});
eventBus.on("hideCreditCardFormError", () => {
  creditCardFormErrorMessage.value = "";
});
onUnmounted(() => {
  eventBus.off("showCreditCardFormError");
  eventBus.off("hideCreditCardFormError");
});

watch(addresses, (newAddresses: MemberAddressTypeExtension[]) => {
  if (isAddressFormExpanded.value) {
    return;
  }
  setMostRecentAddressAsSelected(newAddresses);
});

watch(userSelectedAddressDisplay, async () => {
  if (userSelectedAddressDisplay.value) {
    if (!userBillingCountryCode.value) {
      setBillingCountryCode(userSelectedAddressDisplay.value.countryCode ?? "");
    }
    const newDataAreaId = recheckDataAreaId(userSelectedAddressDisplay.value);
    if (newDataAreaId && paymentProcessingCyberSourceRefs.value?.initialConfigurationLoaded) {
      eventBus.emit("showCreditCardFormError", {
        message: t("shared.payment.bank_card_form.daid_changed"),
      });
      await paymentProcessingCyberSourceRefs.value?.loadPaymentForm(newDataAreaId);
    }
  }
});

defineExpose({
  openDialog,
  closeDialog,
});
</script>

<style lang="scss" scoped>
.vc-button.address-btn {
  @apply p-0 #{!important};
  @media (width <= 400px) {
    @apply w-full;
  }
}

.vc-button {
  @apply py-2.5 #{!important};
}

.vc-button--outline--secondary {
  @apply border-[--color-neutral-a3] border-[1px] rounded-md #{!important};
}

.error-text {
  @apply text-sm text-[--color-danger-450];
}
</style>
